// 提示音：用 Web Audio 实时合成"叮咚"，不依赖音频文件。
// AdminNotificationBell（管理员/代理端）和 WorkspacePage（普通用户）共用同一份实现。
//
// 【浏览器 autoplay 策略】
// AudioContext 在用户手势之外处于 'suspended' 状态，无法播放。
// 旧实现每次调用都 new AudioContext()，socket 事件里 resume() 会被浏览器静默拒绝。
// 新实现：
//   1. 全局单例 _ctx — 只创建一次
//   2. 导出 unlockAudio()，在任意用户手势（click/touchend）中调用，解锁一次即可
//   3. 后续 socket 事件触发的 playChime() 直接复用已解锁的 context，无需再依赖手势

export const supportsAudio =
  typeof window !== 'undefined' &&
  !!(window.AudioContext || window.webkitAudioContext);

let _ctx = null;

function ensureCtx() {
  if (!supportsAudio) return null;
  const Ctor = window.AudioContext || window.webkitAudioContext;
  if (!_ctx || _ctx.state === 'closed') {
    try { _ctx = new Ctor(); } catch { return null; }
  }
  return _ctx;
}

/**
 * 在任意用户手势（click / touchend）里调用，提前解锁 AudioContext。
 * 解锁后，后续从 socket 事件触发的 playChime() 可正常播放。
 */
export function unlockAudio() {
  const ctx = ensureCtx();
  if (ctx && ctx.state === 'suspended') {
    ctx.resume().catch(() => {});
  }
}

export function playChime() {
  const ctx = ensureCtx();
  if (!ctx) return;
  try {
    const doPlay = () => {
      // 叮咚：C6(1046.5Hz) → G5(784Hz) 下行大三度，总时长约 0.45s
      const notes = [
        { f: 1046.50, t: 0.00, d: 0.22 },
        { f: 783.99,  t: 0.18, d: 0.28 },
      ];
      for (const n of notes) {
        const osc  = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = 'sine';
        osc.frequency.value = n.f;
        const start = ctx.currentTime + n.t;
        gain.gain.setValueAtTime(0.0001, start);
        gain.gain.exponentialRampToValueAtTime(0.32, start + 0.02);
        gain.gain.exponentialRampToValueAtTime(0.0001, start + n.d);
        osc.connect(gain).connect(ctx.destination);
        osc.start(start);
        osc.stop(start + n.d + 0.04);
      }
    };
    if (ctx.state === 'suspended') {
      ctx.resume().then(doPlay).catch(() => {});
    } else {
      doPlay();
    }
  } catch { /* 音频不可用时静默 */ }
}
